import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ComposedChart, Line, ReferenceLine } from "recharts";
import { useModuleData } from "@/lib/useData";
import { PALETTE, COLORS, fmt, uniq, CURRENT_YEAR } from "@/lib/api";
import { PageHeader, Panel, Kpi, FilterSelect, ChartTooltip, Loading } from "@/components/dash";
import { DataTable } from "@/components/DataTable";
import { AIInsights } from "@/components/AIInsights";
import { ProductGate } from "@/components/ProductGate";

const sum = (rows) => rows.reduce((a, r) => a + r.value_kt, 0);
const cagr = (a, b, n) => (a && n > 0 ? ((b / a) ** (1 / n) - 1) * 100 : 0);
const grid = <CartesianGrid stroke={COLORS.grid} vertical={false} />;

function DemandView({ product, selector }) {
  const { data, isLoading } = useModuleData("demand", product);
  const [region, setRegion] = useState("all");
  const [app, setApp] = useState("all");
  const rows = useMemo(() => data?.rows || [], [data]);
  const regions = uniq(rows.map((r) => r.region));
  const apps = uniq(rows.map((r) => r.application));
  const years = uniq(rows.map((r) => r.year));
  const filtered = useMemo(() => rows.filter((r) => (region === "all" || r.region === region) && (app === "all" || r.application === app)), [rows, region, app]);

  if (isLoading) return <Loading />;
  const y0 = years[0], yN = years[years.length - 1];
  const lastHist = Math.max(...years.filter((y) => y < CURRENT_YEAR), y0);
  const at = (y, f = () => true) => sum(filtered.filter((r) => r.year === y && f(r)));
  const stacked = years.map((y) => Object.fromEntries([["year", String(y)], ...regions.map((rg) => [rg, at(y, (r) => r.region === rg)])]));
  const line = years.map((y) => ({ year: String(y), Historical: y <= lastHist ? at(y) : null, Forecast: y >= lastHist ? at(y) : null }));
  const appPie = apps.map((a) => ({ name: a, value: at(lastHist, (r) => r.application === a) })).filter((p) => p.value);
  const cagrRows = regions.map((rg) => {
    const f = (r) => r.region === rg;
    return { region: rg, hist: cagr(at(y0, f), at(lastHist, f), lastHist - y0), fcst: cagr(at(lastHist, f), at(yN, f), yN - lastHist) };
  });
  const table = Object.values(filtered.reduce((m, r) => {
    const k = `${r.application}|${r.region}`;
    m[k] = m[k] || { application: r.application, region: r.region };
    m[k][r.year] = r.value_kt;
    return m;
  }, {})).map((o) => ({ ...o, cagr: cagr(o[lastHist], o[yN], yN - lastHist) }));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Market Demand & Forecast" title={product} desc={`Demand by application and region (kt) — ${y0}–${lastHist} actuals, ${lastHist + 1}–${yN} forecast.`}>
        {selector}
        <FilterSelect testId="demand-region-filter" value={region} onChange={setRegion} options={regions} allLabel="All regions" />
        <FilterSelect testId="demand-application-filter" value={app} onChange={setApp} options={apps} allLabel="All applications" />
      </PageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="demand-kpi-current" label={`${lastHist} Demand`} value={fmt(at(lastHist), 1)} unit="kt" />
        <Kpi testId="demand-kpi-forecast" label={`${yN} Forecast`} value={fmt(at(yN), 1)} unit="kt" accent={COLORS.orange} />
        <Kpi testId="demand-kpi-hist-cagr" label={`CAGR ${y0}–${lastHist}`} value={fmt(cagr(at(y0), at(lastHist), lastHist - y0), 2)} unit="%" />
        <Kpi testId="demand-kpi-fcst-cagr" label={`CAGR ${lastHist}–${yN}`} value={fmt(cagr(at(lastHist), at(yN), yN - lastHist), 2)} unit="%" accent={COLORS.cyan} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" title="Market Size by Region" sub="Stacked, kt per year" testId="demand-stacked-bar-chart">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={stacked}>{grid}<XAxis dataKey="year" stroke="#334155" /><YAxis stroke="#334155" />
              <Tooltip content={<ChartTooltip unit="kt" digits={1} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} /><Legend />
              <ReferenceLine x={String(lastHist + 1)} stroke={COLORS.orange} strokeDasharray="4 4" label={{ value: "Forecast →", fill: COLORS.orange, fontSize: 11, position: "insideTopLeft" }} />
              {regions.map((rg, i) => <Bar key={rg} dataKey={rg} stackId="a" fill={PALETTE[i % PALETTE.length]} />)}
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <AIInsights module="demand" product={product} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" title="Historical vs Forecast" sub="Total demand, kt" testId="demand-timeline-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={line}>{grid}<XAxis dataKey="year" stroke="#334155" /><YAxis stroke="#334155" domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip unit="kt" digits={1} />} /><Legend />
              <Line dataKey="Historical" stroke={COLORS.blue} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line dataKey="Forecast" stroke={COLORS.orange} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title={`Application Split · ${lastHist}`} sub="Share of demand" testId="demand-pie-chart">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart><Pie data={appPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="none">
              {appPie.map((p, i) => <Cell key={p.name} fill={PALETTE[i % PALETTE.length]} />)}</Pie>
              <Tooltip content={<ChartTooltip unit="kt" digits={1} />} /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <Panel title="Growth Rate by Region" sub={`CAGR % · ${y0}–${lastHist} vs ${lastHist}–${yN}`} testId="demand-cagr-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={cagrRows}>{grid}<XAxis dataKey="region" stroke="#334155" interval={0} tick={{ fontSize: 10 }} /><YAxis stroke="#334155" unit="%" />
            <Tooltip content={<ChartTooltip unit="%" digits={2} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} /><Legend />
            <Bar dataKey="hist" name={`${y0}–${lastHist}`} fill={COLORS.blue} radius={[3, 3, 0, 0]} />
            <Bar dataKey="fcst" name={`${lastHist}–${yN}`} fill={COLORS.orange} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Demand Data Grid" sub="kt by application × region (orange = forecast)">
        <DataTable testId="demand-data-table" rows={table} columns={[
          { key: "application", label: "Application" }, { key: "region", label: "Region" },
          ...years.map((y) => ({ key: y, label: String(y), num: true, render: (r) => <span className={y > lastHist ? "text-[#F58B23]" : ""}>{fmt(r[y], 2)}</span> })),
          { key: "cagr", label: "Fcst CAGR", num: true, render: (r) => `${fmt(r.cagr, 2)}%` },
        ]} />
      </Panel>
    </div>
  );
}

export default function Demand() {
  return <ProductGate module="demand" eyebrow="Market Demand & Forecast" title="Demand & Forecast">{(p) => <DemandView {...p} />}</ProductGate>;
}
